'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { toast } from 'sonner';
import { GoogleIcon, GitHubIcon, MicrosoftIcon } from '@/components/auth/oauth-icons';
import { VirzaMark } from '@/components/branding/virza-mark';

// Re-exported so existing `@/components/auth/auth-layout` imports of VirzaMark
// keep working. New call sites should import from `@/components/branding/virza-mark`
// directly to avoid pulling in framer-motion / sonner / OAuth helpers.
export { VirzaMark };

// ─── Premium Input (Dark) ────────────────────────────────────────────────────

interface AuthInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  rightElement?: React.ReactNode;
}

export function AuthInput({ label, error, rightElement, className, ...props }: AuthInputProps) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[13px] font-medium text-white/50">{label}</label>
      <div className="relative">
        <input
          className={`h-11 w-full rounded-lg border bg-white/[0.05] px-3.5 text-[15px] text-white transition-all duration-150 placeholder:text-white/25 focus:border-white/20 focus:bg-white/[0.07] focus:ring-2 focus:ring-white/[0.08] focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
            error
              ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20'
              : 'border-white/[0.08]'
          } ${rightElement ? 'pr-11' : ''} ${className || ''}`}
          {...props}
        />
        {rightElement && (
          <div className="absolute top-1/2 right-3 -translate-y-1/2">{rightElement}</div>
        )}
      </div>
      {error && <p className="text-[13px] text-red-400">{error}</p>}
    </div>
  );
}

// ─── Premium Button (Dark) ───────────────────────────────────────────────────

interface AuthButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary';
  loading?: boolean;
  children: React.ReactNode;
}

export function AuthButton({
  variant = 'primary',
  loading,
  children,
  disabled,
  className,
  ...props
}: AuthButtonProps) {
  const base =
    'w-full h-11 px-4 rounded-lg text-[15px] font-medium transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 cursor-pointer';

  const variants = {
    primary: 'bg-white text-zinc-950 hover:bg-white/90 active:scale-[0.98]',
    secondary:
      'bg-white/[0.06] text-white border border-white/[0.08] hover:bg-white/[0.1] hover:border-white/[0.12] active:scale-[0.98]',
  };

  return (
    <button
      className={`${base} ${variants[variant]} ${className || ''}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      ) : null}
      {children}
    </button>
  );
}

// ─── OAuth Divider ───────────────────────────────────────────────────────────

export function AuthDivider() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-white/[0.08]" />
      <span className="text-xs font-medium tracking-wide text-white/20 uppercase">or</span>
      <div className="h-px flex-1 bg-white/[0.08]" />
    </div>
  );
}

// ─── OAuth Buttons ───────────────────────────────────────────────────────────

type OAuthProvider = 'google' | 'github' | 'microsoft';

interface OAuthButtonsProps {
  /** Optional callback for when an OAuth redirect is initiated (disables form). */
  onOAuthStart?: () => void;
  /** Called when bfcache restore resets OAuth state (user pressed back). */
  onOAuthReset?: () => void;
}

export function OAuthButtons({ onOAuthStart, onOAuthReset }: OAuthButtonsProps) {
  const [loadingProvider, setLoadingProvider] = React.useState<OAuthProvider | null>(null);
  const [errorProvider, setErrorProvider] = React.useState<OAuthProvider | null>(null);

  // Reset loading state when page is restored from bfcache (user pressed back)
  React.useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setLoadingProvider(null);
        setErrorProvider(null);
        onOAuthReset?.();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [onOAuthReset]);

  const handleOAuth = async (provider: OAuthProvider) => {
    if (loadingProvider) return;
    setLoadingProvider(provider);
    setErrorProvider(null);
    onOAuthStart?.();

    try {
      // Dynamic import to avoid circular dependency with useAuth
      const { authOAuthActions } = await import('@/hooks/use-auth-oauth');
      const action =
        provider === 'google'
          ? authOAuthActions.signInWithGoogle
          : provider === 'github'
            ? authOAuthActions.signInWithGitHub
            : authOAuthActions.signInWithMicrosoft;
      await action();
      // Browser will redirect — keep loading state visible
    } catch {
      setLoadingProvider(null);
      setErrorProvider(provider);
      onOAuthReset?.();
      const providerLabel =
        provider === 'google' ? 'Google' : provider === 'github' ? 'GitHub' : 'Microsoft';
      toast.error(`Unable to connect to ${providerLabel}. Please try again.`);
    }
  };

  const isDisabled = loadingProvider !== null;

  return (
    <div className="space-y-2.5">
      {/* Google — primary OAuth provider */}
      <button
        type="button"
        onClick={() => handleOAuth('google')}
        disabled={isDisabled}
        className="flex h-11 w-full cursor-pointer items-center justify-center gap-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-[15px] font-medium text-white/80 transition-all duration-150 hover:border-white/[0.14] hover:bg-white/[0.08] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Continue with Google"
      >
        {loadingProvider === 'google' ? (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <GoogleIcon size={16} />
        )}
        {loadingProvider === 'google' ? 'Redirecting…' : 'Continue with Google'}
      </button>

      {/* GitHub — secondary OAuth provider */}
      <button
        type="button"
        onClick={() => handleOAuth('github')}
        disabled={isDisabled}
        className="flex h-11 w-full cursor-pointer items-center justify-center gap-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-[15px] font-medium text-white/80 transition-all duration-150 hover:border-white/[0.14] hover:bg-white/[0.08] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Continue with GitHub"
      >
        {loadingProvider === 'github' ? (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <GitHubIcon size={16} />
        )}
        {loadingProvider === 'github' ? 'Redirecting…' : 'Continue with GitHub'}
      </button>

      {/* Microsoft — school/university accounts (.edu) */}
      <button
        type="button"
        onClick={() => handleOAuth('microsoft')}
        disabled={isDisabled}
        className="flex h-11 w-full cursor-pointer items-center justify-center gap-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-[15px] font-medium text-white/80 transition-all duration-150 hover:border-white/[0.14] hover:bg-white/[0.08] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Continue with Microsoft"
      >
        {loadingProvider === 'microsoft' ? (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <MicrosoftIcon size={16} />
        )}
        {loadingProvider === 'microsoft' ? 'Redirecting…' : 'Continue with Microsoft'}
      </button>

      {/* Error recovery hint */}
      {errorProvider && (
        <p className="text-center text-xs text-red-400/70">
          Connection failed. Check your network and try again.
        </p>
      )}
    </div>
  );
}

// ─── Password Strength (Dark) ────────────────────────────────────────────────

interface PasswordRequirement {
  label: string;
  met: boolean;
}

function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Lowercase letter', met: /[a-z]/.test(password) },
    { label: 'Uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'Number', met: /\d/.test(password) },
    { label: 'Special character', met: /[^A-Za-z0-9]/.test(password) },
  ];
}

function RequirementIcon({ met }: { met: boolean }) {
  return (
    <svg
      className={`size-3.5 shrink-0 transition-colors duration-200 ${
        met ? 'text-emerald-400' : 'text-white/20'
      }`}
      viewBox="0 0 16 16"
      fill="none"
    >
      {met ? (
        <path
          d="M13.25 4.75L6 12L2.75 8.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <circle cx="8" cy="8" r="3" fill="currentColor" />
      )}
    </svg>
  );
}

// k-Anonymity breach check — only first 5 chars of SHA-1 are sent to the API
async function checkPasswordBreach(password: string): Promise<number> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();

    const prefix = hashHex.slice(0, 5);
    const suffix = hashHex.slice(5);

    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { 'Add-Padding': 'true' },
    });
    if (!res.ok) return 0;

    const text = await res.text();
    const match = text.split('\n').find((line) => line.startsWith(suffix));
    if (!match) return 0;

    return parseInt(match.split(':')[1].trim(), 10);
  } catch {
    return 0;
  }
}

export function PasswordStrength({ password }: { password: string }) {
  const [breachCount, setBreachCount] = React.useState(0);
  const [checkingBreach, setCheckingBreach] = React.useState(false);

  // Debounced breach check — only fires after user stops typing for 600ms
  React.useEffect(() => {
    if (!password || password.length < 8) {
      setBreachCount(0);
      return;
    }
    setCheckingBreach(true);
    const timer = setTimeout(async () => {
      const count = await checkPasswordBreach(password);
      setBreachCount(count);
      setCheckingBreach(false);
    }, 600);
    return () => {
      clearTimeout(timer);
      setCheckingBreach(false);
    };
  }, [password]);

  if (!password) return null;

  const requirements = getPasswordRequirements(password);
  const metCount = requirements.filter((r) => r.met).length;
  const allMet = metCount === requirements.length;

  // Score: 0-5 based on requirements met, penalized if breached
  const score = breachCount > 0 ? Math.min(metCount, 1) : metCount;

  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
  const colors = ['rgba(255,255,255,0.06)', '#EF4444', '#F59E0B', '#3B82F6', '#22C55E', '#10B981'];

  return (
    <div className="space-y-2.5">
      {/* Strength bar */}
      <div className="space-y-1.5">
        <div className="flex gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-full transition-colors duration-200"
              style={{
                backgroundColor: i < score ? colors[score] : 'rgba(255,255,255,0.06)',
              }}
            />
          ))}
        </div>
        {score > 0 && (
          <p className="text-xs" style={{ color: colors[score] }}>
            {labels[score]}
          </p>
        )}
      </div>

      {/* Requirements checklist */}
      {!allMet && (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
          {requirements.map((req) => (
            <li
              key={req.label}
              className={`flex items-center gap-1.5 text-xs transition-colors duration-200 ${
                req.met ? 'text-emerald-400' : 'text-white/30'
              }`}
            >
              <RequirementIcon met={req.met} />
              {req.label}
            </li>
          ))}
        </ul>
      )}

      {/* Breach warning */}
      {breachCount > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.05] px-2.5 py-2">
          <svg className="mt-0.5 size-4 shrink-0 text-amber-500" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 1L1 14h14L8 1z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <path d="M8 6v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
          </svg>
          <p className="text-xs leading-relaxed text-amber-400">
            This password appeared in {breachCount.toLocaleString()} data breach
            {breachCount !== 1 ? 'es' : ''}. Choose a different password.
          </p>
        </div>
      )}

      {/* Checking indicator */}
      {checkingBreach && allMet && (
        <p className="flex items-center gap-1.5 text-xs text-white/30">
          <span className="size-3 animate-spin rounded-full border-2 border-white/10 border-t-white/40" />
          Checking against known breaches...
        </p>
      )}
    </div>
  );
}

// ─── OTP Input (Dark) ────────────────────────────────────────────────────────

interface OTPInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  error?: boolean;
  disabled?: boolean;
  length?: number;
  /** Show a visual separator between the two halves (e.g. digits 3-4). Defaults to false. */
  showSeparator?: boolean;
}

export function OTPInput({
  value,
  onChange,
  error,
  disabled,
  length = 6,
  showSeparator = false,
}: OTPInputProps) {
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, char: string) => {
    if (!/^\d*$/.test(char)) return;
    const next = [...value];
    next[index] = char.slice(-1);
    onChange(next);
    if (char && index < length - 1) {
      setTimeout(() => refs.current[index + 1]?.focus(), 0);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const next = [...value];
    pasted.split('').forEach((char, i) => {
      next[i] = char;
    });
    onChange(next);
    const focusIndex = Math.min(pasted.length, length - 1);
    setTimeout(() => refs.current[focusIndex]?.focus(), 0);
  };

  return (
    <div className="flex justify-center gap-2.5" onPaste={handlePaste}>
      {value.map((digit, i) => (
        <React.Fragment key={i}>
          {showSeparator && i === Math.floor(length / 2) && (
            <div
              aria-hidden="true"
              role="presentation"
              className="flex w-3 items-center justify-center text-white/15"
            >
              –
            </div>
          )}
          <motion.input
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={disabled}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: i * 0.04 }}
            className={`h-[52px] w-11 rounded-lg border-2 text-center text-lg font-semibold transition-all duration-200 focus:outline-none ${
              error
                ? 'border-red-500/50 bg-red-500/[0.06] text-red-400'
                : digit
                  ? 'border-white/20 bg-white/[0.08] text-white focus:border-white/30'
                  : 'border-white/[0.08] bg-white/[0.03] text-white hover:border-white/15 focus:border-white/25 focus:bg-white/[0.06]'
            } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Success / Error States (Dark) ───────────────────────────────────────────

export function SuccessState({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="text-center"
    >
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
        <svg
          className="h-7 w-7 text-emerald-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-semibold tracking-[-0.01em] text-white">{title}</h2>
      <p className="mt-2 text-[15px] leading-relaxed text-white/50">{description}</p>
      {children && <div className="mt-6 flex flex-col gap-3">{children}</div>}
    </motion.div>
  );
}

export function ErrorState({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10">
        <svg
          className="h-7 w-7 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h2 className="text-2xl font-semibold tracking-[-0.01em] text-white">{title}</h2>
      <p className="mt-2 text-[15px] leading-relaxed text-white/50">{description}</p>
      {children && <div className="mt-6 flex flex-col gap-3">{children}</div>}
    </div>
  );
}

// ─── Unified Auth Layout (Dark) ──────────────────────────────────────────────

const pageEntryVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] as const },
};

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen flex-col bg-zinc-950">
      {/* Ambient glow */}
      <div className="pointer-events-none fixed inset-0">
        <div
          className="absolute top-0 left-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/4 rounded-full"
          style={{
            background:
              'radial-gradient(ellipse, rgba(120,119,198,0.07) 0%, rgba(120,119,198,0.02) 40%, transparent 70%)',
          }}
        />
      </div>

      {/* Dot pattern */}
      <svg className="pointer-events-none fixed inset-0 h-full w-full opacity-[0.02]">
        <defs>
          <pattern id="auth-dots" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.5" fill="white" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#auth-dots)" />
      </svg>

      {/* Content */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 py-12">
        <motion.div className="w-full max-w-[380px]" {...pageEntryVariants}>
          {/* Logo */}
          <div className="mb-10 flex justify-center">
            <Link
              href="/"
              className="group inline-flex flex-col items-center gap-1.5"
              aria-label="Virza home"
            >
              <div className="relative">
                <div
                  className="absolute inset-0 opacity-20 blur-xl"
                  style={{
                    background:
                      'radial-gradient(circle, rgba(120,119,198,0.5) 0%, transparent 70%)',
                    transform: 'scale(4)',
                  }}
                />
                <div className="relative transition-transform group-hover:scale-105">
                  <VirzaMark variant="dark" size={56} />
                </div>
              </div>
            </Link>
          </div>

          {children}
        </motion.div>
      </div>

      {/* Footer */}
      <div className="relative z-10 px-6 py-6 text-center">
        <p className="text-xs leading-relaxed text-white/20">
          By continuing, you agree to our{' '}
          <a
            href="https://virza.io/terms"
            className="text-white/30 transition-colors hover:text-white/50"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms
          </a>{' '}
          and{' '}
          <a
            href="https://virza.io/privacy"
            className="text-white/30 transition-colors hover:text-white/50"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
